import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import LocationAutocompleteInput from '@components/fields/LocationAutocompleteInput';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import {
  CareProfileAgeEnum,
  CareProfileFragment as CareProfileType,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse, ThingWithErrors } from '@utils/formUtils';

type FormData = {
  age: CareProfileAgeEnum | null;
  careProfilePlaceId: string | null;
  name: string;
};

type Props = {
  autofocus: boolean;
  buttonExpand?: 'full' | 'block';
  careProfile: CareProfileType;
  onCancel?: () => void;
  onError?: () => void;
  onSuccess: () => void;
  saveKey?: string;
  skipCancel?: boolean;
};

const CareProfileBasicForm = ({
  autofocus,
  buttonExpand,
  careProfile,
  onCancel,
  onError,
  onSuccess,
  saveKey,
  skipCancel = false
}: Props) => {
  const initialValues: FormData = {
    age: careProfile.age ?? null,
    careProfilePlaceId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS
      ? 'ChIJfX6-HwSbf4gR4Aa9pkeWBLY'
      : careProfile.location.placeId,
    name: careProfile.name ?? ''
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema = yup
    .object()
    .shape({
      age: yup.string().required(intl.formatMessage({ id: 'errors.careProfile.age.blank' })),
      careProfilePlaceId: yup.string().required(intl.formatMessage({ id: 'errors.careProfile.placeId.blank' })),
      name: yup.string().required(intl.formatMessage({ id: 'errors.careProfile.name.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateCareProfile({
      variables: {
        ...form.getValues(),
        id: careProfile.id,
        placeId: form.getValues().careProfilePlaceId
      }
    }).then(response => {
      const data = [response.data?.careProfileUpdate] as ThingWithErrors[];
      handleMutationResponse<FormData>({
        data,
        intl,
        onError,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [careProfile.id, form, intl, onError, onSuccess, updateCareProfile]);

  const ageOptions = useMemo(() => {
    const options = [
      CareProfileAgeEnum.sixty_five_and_older,
      CareProfileAgeEnum.eighteen_to_sixty_four,
      CareProfileAgeEnum.under_eighteen,
      CareProfileAgeEnum.deceased
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.careProfile.age.${option}` }),
      value: option
    }));
  }, [intl]);

  return (
    <Form data-test="care-profile-basic-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        aria-required="true"
        autofocus={autofocus}
        control={form.control}
        data-test="name-input"
        error={form.formState.errors.name}
        label={intl.formatMessage({ id: 'forms.careProfile.nameLabel' })}
        name="name"
        required
      />
      <Select<FormData>
        control={form.control}
        data-test="age-input"
        error={form.formState.errors.age}
        label={intl.formatMessage({ id: 'forms.careProfile.ageLabel' })}
        name="age"
        options={ageOptions}
        required
        tabIndex={0}
      />
      <LocationAutocompleteInput
        control={form.control}
        error={form.formState.errors.careProfilePlaceId}
        label={intl.formatMessage({ id: 'forms.careProfile.locationLabel' })}
        location={{ address: careProfile.location.address, placeId: careProfile.location.placeId }}
        name="careProfilePlaceId"
        required
        subLabel={intl.formatMessage({ id: 'forms.careProfile.locationSublabel' })}
      />
      <FormButtons
        expand={buttonExpand}
        hookForm={form}
        onCancel={onCancel}
        saveKey={saveKey}
        skipCancel={skipCancel}
      />
    </Form>
  );
};

export default CareProfileBasicForm;
